/**
 * Masaya Hospitality — furnished ROOM/UNIT listings.
 *
 * Each entry is an individual professionally managed furnished PRIVATE ROOM
 * located within a shared Masaya home in the Austin area (NOT a whole house).
 *
 * ➜ TO ADD A NEW UNIT: copy one object in UNITS, give it a unique `slug`,
 *   set the `airbnbId` + `airbnbUrl`, and list its Airbnb photo UUIDs.
 *   Images are pulled directly from Airbnb's CDN via the `aimg()` helper.
 *
 * `img(uuid)` builds a URL using the unit's own airbnbId folder.
 * For photos that live under a different listing's folder, use `aimg(folderId, uuid)`.
 */

const aimg = (folderId, uuid) =>
  `https://a0.muscache.com/im/pictures/hosting/Hosting-${folderId}/original/${uuid}.jpeg?im_w=1200`;

// Shared amenity + rule sets (reused across units for consistency)
const BASE_AMENITIES = [
  "Furnished Bedroom",
  "High-Speed Wi-Fi",
  "Full Kitchen Access",
  "Laundry Access",
  "Free Parking",
  "Smart-Lock Self Check-in",
  "Utilities Included",
  "Dedicated Workspace",
  "Air Conditioning",
];

const HOUSE_RULES = [
  "Designed for extended stays (30+ nights preferred)",
  "Self check-in after 3:00 PM · checkout by 11:00 AM",
  "No smoking indoors",
  "Quiet hours to respect fellow housemates",
  "No parties or events",
  "Shared common areas — please tidy up after use",
];

// Nearby employers/attractions per shared home
const NEARBY = {
  techRidge: ["The Domain (~5 mi)", "Downtown Austin (~15 mi)", "H-E-B & Walmart nearby", "Tech Ridge employer corridor", "Tech Ridge Bay bus stop (0.4 mi)"],
  techCorridor: ["Applied Materials (~1 mi)", "Samsung Austin (~3 mi)", "Tech Ridge (~6 mi)", "Downtown Austin (~11 mi)"],
  techHub: ["Dell", "Samsung Austin", "Applied Materials", "Tech Ridge corridor", "Downtown Austin"],
  harrisBranch: ["Harris Branch Park (adjacent)", "Applied Materials, Dell & Samsung", "Tech Ridge corridor", "Downtown Austin"],
  pioneer: ["Samsung Austin (~1.5 mi)", "Pioneer Crossing Park (steps away)", "Dell & Applied Materials", "H-E-B (~4.2 mi)"],
  roundRock: ["Pfluger Heights Park (steps away)", "Springbrook Glen Community Park (~10 min walk)", "Dell Round Rock", "Samsung Austin", "Tech Ridge corridor", "Downtown Austin"],
};

const AIRBNB = (id) => `https://www.airbnb.com/rooms/${id}`;

export const UNITS = [
  {
    slug: "bright-airy-tech-ridge",
    airbnbId: "1040698003419704883",
    name: "Bright & Airy Room",
    area: "Tech Ridge, North Austin",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A light-filled private room in a quiet Tech Ridge home, with a walk-in closet and dedicated workspace — ideal for longer stays.",
    description:
      "A bright, elegantly furnished private room in a calm Tech Ridge home, featuring a comfortable queen bed, a generous walk-in closet, and a dedicated workspace. You'll share the fully equipped kitchen, living areas, and laundry with a small number of respectful long-stay housemates.",
    photos: ["6ecb59ba-7bf6-4667-82d2-7b55757444b9", "1985e858-7b10-43e2-a5b5-5092075ab080", "012ea15c-d09b-47c8-920e-18fada5787b4", "1bef3fcc-1c50-4c5c-89a5-a6039af09f42", "f3469508-9b50-406f-aca3-5239405c6f4f"],
    nearby: NEARBY.techRidge,
  },
  {
    slug: "bright-grey-tech-hub",
    airbnbId: "1402528887563348337",
    name: "Bright Grey Room",
    area: "North Austin · Tech Corridor",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A modern, stylish room in a five-bedroom home minutes from Applied Materials and Samsung — perfect for tech professionals.",
    description:
      "A crisp, modern private room in a quiet five-bedroom home built for professionals and long-term stays. Enjoy fast Wi-Fi and a lockable door, plus shared access to a fully equipped kitchen, two dining areas, two living rooms, and laundry.",
    photosMixed: [["1402528887563348337", "dbf06ffe-9af1-4b40-a7a6-e800294f6f25"], ["1402528887563348337", "fecd8023-caa2-458b-9858-dd2c1e8028d6"], ["1372143366661638829", "02a50587-39b1-4765-a2dc-2df89c7d9871"], ["1372143366661638829", "c7040a5a-e1dc-4b96-9ed1-203e4eb3c04c"], ["1402528887563348337", "65ddf12c-0927-49ae-93ea-08b87f8b5931"]],
    nearby: NEARBY.techCorridor,
  },
  {
    slug: "cozy-tech-ridge",
    airbnbId: "1090581280564599035",
    name: "Cozy Room",
    area: "Tech Ridge, North Austin",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A warm, cozy private room in a serene Tech Ridge subdivision, close to shopping, dining, and The Domain.",
    description:
      "A comfortable, welcoming private room in a peaceful Tech Ridge home. Thoughtfully furnished with a queen bed, walk-in closet, and workspace, with shared access to the kitchen, living spaces, and laundry — a true home away from home for extended stays.",
    photos: ["92e1e8b3-e60f-484c-9282-614c13fe061d", "230fd5db-5fd4-4846-90a4-de8e1e9c1f68", "45fc9ac9-e710-4d97-84ad-b6f91ed4de75", "60a2ded4-9940-4aa3-9031-ae00aaa1b35e", "f169cca4-1807-46b0-a08f-8af057700c10"],
    nearby: NEARBY.techRidge,
  },
  {
    slug: "economy-tech-hub",
    airbnbId: "1404713921502251537",
    name: "Economy Room",
    area: "North Austin · Near Dell & Samsung",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A smart, affordable private room ideal for solo travelers and interns, with a shared game room and backyard fire pit.",
    description:
      "A compact, functional private room ideal for solo travelers, remote workers, and interns seeking an affordable, convenient stay. Shared access to a fully equipped kitchen, dining and living areas, laundry, plus a game room and backyard fire pit.",
    photos: ["26ba69af-f9b5-4d17-a264-4649d07ce8af", "1053015e-b3b1-4882-9a62-987f249057e2", "d6399377-a376-4002-a91b-dfc41b3f29d2", "996d42f7-de59-4462-ac9a-1cb03bb47682", "a28190eb-e6e8-4ef5-8810-f68f3dab8dca"],
    nearby: NEARBY.techHub,
  },
  {
    slug: "light-bright-tech-hub",
    airbnbId: "1404739490193951612",
    name: "Light & Bright Room",
    area: "North Austin · Tech Corridor",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "An airy, light-filled room with large windows and room-darkening shades — great for remote workers and students.",
    description:
      "A refreshing, light-filled private room with large windows and room-darkening shades, designed for comfort and focus. Perfect for remote workers, students, and travelers, with shared access to the home's kitchen, living areas, and laundry.",
    photos: ["7507bd09-dc95-4560-8630-de7e7a16cf1e", "0c66158f-aa69-4dbc-82aa-028952dd4991", "3b368f3e-139c-4796-8737-61616fc1240f", "e706bd76-2129-4075-9824-fe7ca40db743", "d47f4457-ca31-4869-985b-960df06bdffc"],
    nearby: NEARBY.techHub,
  },
  {
    slug: "master-bedroom-tech-ridge",
    airbnbId: "1040692119039579455",
    name: "Master Bedroom · En-suite",
    area: "Tech Ridge, North Austin",
    bathroom: "Private Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "An expansive master room with a private en-suite bathroom and walk-in closet — maximum privacy and comfort.",
    description:
      "A spacious, luxuriously furnished master room with an elegant private en-suite bathroom and a generously sized walk-in closet. The most private option in a calm Tech Ridge home, with shared access to the kitchen, living spaces, and backyard.",
    photos: ["086c5590-806a-4d9c-9360-f6bf0f368e8e", "f9f6c790-5602-41b5-8129-071481e52f3c", "54ef9de9-3776-4073-9605-51cf0cd4c2d6", "f9f640f1-9ab8-4776-9ad2-feae2272a04c", "5deb09a1-4947-41b5-8617-7b1f84645dc2"],
    nearby: NEARBY.techRidge,
  },
  {
    slug: "modern-tech-hub",
    airbnbId: "1404745420562818246",
    name: "Modern Room",
    area: "Northeast Austin · Harris Branch",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A sleek, contemporary room next to Harris Branch Park, with a shared jacuzzi, fire pit, and game room.",
    description:
      "A sleek, contemporary private room with a clean, modern design — ideal for professionals and long-term travelers. Shared amenities include a fully equipped kitchen, living rooms, laundry, plus a jacuzzi, fire pit, and game room on-site.",
    photos: ["e60db914-a882-46c0-828d-e744b46761fe", "c94a5bed-b5e0-4082-b257-05f3c5b58ec9", "ad60e1f0-691d-4b71-a73a-fd18526febda", "61668a61-ac30-47c2-b46d-e27498e89d17", "56812d8b-33f1-4bad-8e18-f35f55d17fde"],
    nearby: NEARBY.harrisBranch,
  },
  {
    slug: "radiant-tech-ridge",
    airbnbId: "1075960908825123991",
    name: "Radiant Room",
    area: "Tech Ridge, North Austin",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A newly renovated, sunlit room in a serene neighborhood, minutes from the mall, shopping, and grocery stores.",
    description:
      "A newly renovated, sunlit private room in a quiet Tech Ridge home, just minutes from shopping and grocery stores. Fully furnished with a queen bed, workspace, and walk-in closet, with shared access to the kitchen, living areas, and laundry.",
    photos: ["2d125f43-b75c-476f-a796-e0ae11f25ac4", "bf636f67-9e0c-4727-8ca3-8ca6a319101e", "236bdf7d-267e-4fd2-bbbd-22a613e32139", "4f36adb9-b3db-4728-87bc-df28f0578979", "16cd1c46-09bf-40fd-8917-fcd64fc8e5ae"],
    nearby: NEARBY.techRidge,
  },
  {
    slug: "king-suite-tech-hub",
    airbnbId: "1372143366661638829",
    name: "Spacious King Suite · En-suite",
    area: "North Austin · Tech Corridor",
    bathroom: "Private Bathroom",
    bed: "1 King Bed",
    parking: true,
    short: "An elegant king suite — the only room in the home with a private bathroom and a huge closet.",
    description:
      "An elegant king-size suite and the only room in the home with a private bathroom, offering maximum privacy and comfort along with a huge closet. Set in a quiet five-bedroom home near major tech employers, with shared kitchen and living spaces.",
    photos: ["21cdcdf2-10b6-447a-bbb1-2f4a84dae12c", "02a50587-39b1-4765-a2dc-2df89c7d9871", "c7040a5a-e1dc-4b96-9ed1-203e4eb3c04c", "b0023ed7-6ecf-4640-8539-fec322295a41", "2180307f-bc9f-462c-92f2-1d2553843949"],
    nearby: NEARBY.techCorridor,
  },
  {
    slug: "black-white-tech-ridge",
    airbnbId: "1624304721931920335",
    name: "Black & White Room",
    area: "Tech Ridge, North Austin",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A stylish black-and-white room in a quiet shared home steps from Pioneer Crossing Park, near Samsung.",
    description:
      "A quiet, stylish black-and-white room with a clean modern vibe in a calm shared home with two other housemates. Steps from Pioneer Crossing Park and close to Samsung and major tech employers, with a private workspace and shared kitchen.",
    photosMixed: [["1624304721931920335", "dc5f9b2f-027a-4a65-992c-ebba2fc4c8ef"], ["1624270926210526115", "ac976c4c-1253-4840-8c0d-2ccbcb64a53e"], ["1624270926210526115", "be64cf5a-fdae-4c2f-8f60-3fe098dd34b7"], ["1624304721931920335", "15b4ad7c-71a2-4bf7-ba25-84cb2c971773"], ["1624270926210526115", "f24ddf28-93f3-49ae-aaad-77cccd94c867"]],
    nearby: NEARBY.pioneer,
  },
  {
    slug: "master-tech-ridge-pioneer",
    airbnbId: "1624270926210526115",
    name: "Pioneer Master Bedroom",
    area: "Tech Ridge, North Austin",
    bathroom: "Private Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A spacious master room in a peaceful shared home near Pioneer Crossing Park and Samsung.",
    description:
      "A spacious, peaceful master room in a quiet shared home with two other housemates, close to Pioneer Crossing Park and Samsung. Includes a private workspace and comfortable queen bed, with shared access to a full kitchen, living areas, and laundry.",
    photosMixed: [["1624270926210526115", "ac976c4c-1253-4840-8c0d-2ccbcb64a53e"], ["1624270926210526115", "be64cf5a-fdae-4c2f-8f60-3fe098dd34b7"], ["1624270926210526115", "f24ddf28-93f3-49ae-aaad-77cccd94c867"], ["1624270926210526115", "075b825f-4f87-4631-aae1-36303b013482"], ["1624270926210526115", "ffe800e0-1c83-42fb-b855-6485e440779b"]],
    nearby: NEARBY.pioneer,
  },
  {
    slug: "minimalist-tech-ridge",
    airbnbId: "1624298018944766171",
    name: "Minimalist Room",
    area: "Tech Ridge, North Austin",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A clean, minimalist room with soft lighting and a dedicated workspace — perfect for rest and productivity.",
    description:
      "A clean, minimalist private room with soft lighting and a dedicated workspace, built for rest and productivity. Set in a quiet shared home with two other housemates near Samsung and Pioneer Crossing Park, with shared kitchen and living areas.",
    photosMixed: [["1624298018944766171", "9438ba47-715e-44c1-b011-1febd02f95ff"], ["1624298018944766171", "c1dd0e23-8e3e-4cca-a189-0b8460f61a9f"], ["1624298018944766171", "0b0a1c16-2791-43ae-9f9e-404818a41a57"], ["1624270926210526115", "ac976c4c-1253-4840-8c0d-2ccbcb64a53e"], ["1624270926210526115", "be64cf5a-fdae-4c2f-8f60-3fe098dd34b7"]],
    nearby: NEARBY.pioneer,
  },
  {
    slug: "studio-loft-round-rock",
    airbnbId: "1732879176565162554",
    name: "Private Studio-Style Loft",
    area: "Round Rock, TX · North Austin",
    bathroom: "Private Bathroom",
    bed: "2 Full Beds + Sofa Bed",
    parking: true,
    guests: 4,
    short: "An entire private second-floor loft with a bedroom, private bathroom, living area, kitchenette, workspace, and TV — one of our most private, spacious options.",
    description:
      "Enjoy an entire private second-floor retreat designed for comfortable extended stays in the North Austin and Round Rock area. Includes a private bedroom with two full beds, private attached bathroom, a separate living room with a sofa bed, a private kitchenette (mini fridge, microwave, French press, electric kettle), dining area, TV, and dedicated workspace. Guests also have access to the full kitchen and laundry downstairs.",
    photos: ["b385b8dc-a864-4e88-899e-b127069741ef", "9c76800e-97cc-4f0c-838d-449764f518b9", "67094192-0475-4cd4-8910-1ebbad0ec957", "e74e2803-806b-496b-aeab-9cc15f6e15d8", "acd38951-ec2b-4615-9ffc-9ad6b8a8efbf"],
    nearby: NEARBY.roundRock,
  },
  {
    slug: "master-suite-round-rock",
    airbnbId: "1732890052798464389",
    name: "Master Suite",
    area: "Round Rock, TX · North Austin",
    bathroom: "Private Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A spacious private suite with an ensuite bathroom, walk-in closet, workspace, and access to a fenced backyard with a gas grill.",
    description:
      "A premium furnished suite for professionals and extended-stay guests seeking more privacy. The private room includes an attached ensuite bathroom, a walk-in closet, dedicated workspace, and high-speed Wi-Fi. Shared amenities include a full kitchen, living areas, and a fenced backyard with outdoor seating and a gas grill.",
    photos: ["4cd76c8a-a35f-420a-b26b-b2d3543effd5", "6c4a4d5c-4143-43fa-a383-023407f96923", "9659ceb3-265b-48fc-9eb2-7d4848553358", "a2b8ab92-2c8d-4f97-9cea-567465bb535e", "5620bbc0-0839-4706-b96c-7ad57ce81314"],
    nearby: NEARBY.roundRock,
  },
  {
    slug: "harmony-room-round-rock",
    airbnbId: "1732893644021912691",
    name: "Harmony Room",
    area: "Round Rock, TX · North Austin",
    bathroom: "Shared Bathroom",
    bed: "1 Queen Bed",
    parking: true,
    short: "A comfortable furnished private room with a queen bed, workspace, Wi-Fi, kitchen and laundry access, and free parking — ideal for interns and professionals.",
    description:
      "The Harmony Room is a cozy, comfortable furnished retreat with a queen-size bed, dedicated workspace, and a bedroom door lock for privacy. Guests have access to a shared bathroom, high-speed Wi-Fi, a fully equipped kitchen, laundry, and welcoming shared living spaces — ideal for business travelers, interns, travel nurses, and long-stay professionals.",
    photos: ["c58578c2-2ad6-4bc2-8f97-a888084cab3e", "a2bc4120-0de0-46fd-b996-8f747379d9a0", "01fc448a-cd04-4e43-90a3-278130ddd6bb", "e24dc559-f958-46c5-b4ae-51573c756be5", "935a0be2-f09e-4cfd-946b-665a7ecb3d00"],
    nearby: NEARBY.roundRock,
  },
  {
    slug: "harper-bedroom-round-rock",
    airbnbId: "1732899195723661704",
    name: "Harper Bedroom",
    area: "Round Rock, TX · North Austin",
    bathroom: "Shared Bathroom · Only 1 Other Guest",
    bed: "1 Queen Bed",
    parking: true,
    short: "A stylish furnished private room with a queen bed and a bathroom shared with only one other guest — a comfortable balance of affordability and privacy.",
    description:
      "The Harper Bedroom is a cozy, stylish furnished retreat featuring a comfortable queen-size bed. Guests have access to a bathroom shared with only one other guest — offering added privacy and convenience — plus high-speed Wi-Fi, a fully equipped kitchen, laundry, and inviting shared living spaces. Ideal for business travelers, travel nurses, interns, and extended stays.",
    photos: ["487261e7-bca1-485b-9eb9-fa55b32c4741", "4f690629-a994-4119-ae4a-11149777a5b4", "3ddec24f-e7ba-4c78-8f40-bcaec7517df7", "6a9843a9-4b21-4a22-bdc3-4ab3fd355d6e", "12399a78-9fe4-4606-ad7c-5d64003f7398"],
    nearby: NEARBY.roundRock,
  },
].map((u) => {
  const gallery = u.photosMixed
    ? u.photosMixed.map(([folder, uuid]) => aimg(folder, uuid))
    : u.photos.map((uuid) => aimg(u.airbnbId, uuid));
  const amenities = [
    u.bathroom,
    ...BASE_AMENITIES,
    ...(u.parking ? [] : []),
  ];
  const isPremium = u.bathroom.toLowerCase().includes("private");
  return {
    ...u,
    cover: gallery[0],
    gallery,
    amenities,
    houseRules: HOUSE_RULES,
    airbnbUrl: AIRBNB(u.airbnbId),
    category: isPremium ? "premium" : "private-room",
    isPremium,
  };
});
